















































import { computed, defineComponent, ref } from "@vue/composition-api";
import { PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import { PersonalInformation } from "@scrile/api-provider/dist/api/PersonalInformationProvider";
import { UserVerification, UserVerificationStatus } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useUserVerification from "@/hooks/useUserVerification";
import providers from "@/lib/api";
import { $t } from "@/lib/i18nHelpers";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseSpinner from "@/components/BaseSpinner";
import CardInfo from "@/components/CardInfo";
import PageTitle from "@/components/PageTitle";
import getComponent from "../integrations";

interface Info {
  title: string;
  items: InfoItem[];
}

interface InfoItem {
  name: string;
  value: string | null;
  icon?: string;
}

export default defineComponent({
  name: "PayeeInfoView",
  components: { CardInfo, PageTitle, BaseIcon, BaseSpinner, BaseIconButton },
  setup() {
    const { authUserStrict, userVerificationStatus: authUserVerificationStatus } = useAuthUser();
    const { userVerificationSearch } = useUserVerification();
    const loading = ref(false);
    const personalInformation = ref<PersonalInformation | null>(null);
    const userVerificationStatus = ref<UserVerification | null>(null);
    const payeeInformation = ref<PayeeInformation | null>(null);
    const showWaitingMessage = computed(
      () => authUserVerificationStatus.value === UserVerificationStatus.WAITING_FOR_APPROVAL
    );

    const info = computed<Info[]>(() => {
      if (!personalInformation.value || !userVerificationStatus.value || !payeeInformation.value) return [];
      const integration = getComponent(payeeInformation.value.payoutSystem);
      const listField = integration.listField;
      const formFields = integration.formFields;
      const systemInfo: InfoItem[] = formFields.reduce<InfoItem[]>((out, field) => {
        if (payeeInformation.value?.payoutSystem) {
          out.push({
            name: field.label ?? "",
            value: (payeeInformation.value as any)[payeeInformation.value.payoutSystem][field.name],
          });
        }
        return out;
      }, []);

      return [
        {
          title: $t("Payee information"),
          items: [
            {
              name: $t("First name"),
              value: personalInformation.value.firstName,
            },
            {
              name: $t("Last name"),
              value: personalInformation.value.lastName,
            },
            {
              name: $t("Phone number"),
              value: personalInformation.value.phone,
            },
          ],
        },
        {
          title: $t("Age"),
          items: [
            {
              name: $t("Photo or scan of your ID"),
              value: userVerificationStatus.value.idPhoto ? $t("Attached") : $t("Not attached"),
              icon: userVerificationStatus.value.idPhoto ? "sci-lock" : undefined,
            },
            {
              name: $t("Your photo with ID"),
              value: userVerificationStatus.value.photoWithId ? $t("Attached") : $t("Not attached"),
              icon: userVerificationStatus.value.idPhoto ? "sci-lock" : undefined,
            },
          ],
        },
        {
          title: $t("Payout system"),
          items: [
            {
              name: $t("Payout system"),
              value: listField?.text ?? "",
            },
            {
              name: $t("Currency"),
              value: payeeInformation.value.currency,
            },
            ...systemInfo,
          ],
        },
      ];
    });

    const getUserVerification = async () => {
      let data = await userVerificationSearch({ userIds: [authUserStrict.value.id] });
      userVerificationStatus.value = data.result.find(i => i.userId === authUserStrict.value.id) || null;
    };

    const getData = async () => {
      try {
        loading.value = true;
        await Promise.all([
          providers.PersonalInformationProvider.find({ id: authUserStrict.value.id }).then(
            r => (personalInformation.value = r)
          ),
          getUserVerification(),
          providers.PayeeInformationProvider.find({ id: authUserStrict.value.id }).then(
            r => (payeeInformation.value = r)
          ),
        ]);
      } finally {
        loading.value = false;
      }
    };

    getData();

    return {
      loading,
      showWaitingMessage,
      info,
    };
  },
});
